import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getFeePositionLatest } from '../../api/feeTier';
import { TargetIcon, BarChartIcon, ClockIcon, TrendingUpIcon } from '../shared/Icons';
import StandaloneSection from '../shared/StandaloneSection';
import '../shared/MetricCards.css';

const formatCurrency = (value: string | null | undefined): string => {
  if (value === null || value === undefined) return 'n/a';
  const num = parseFloat(value);
  if (Number.isNaN(num)) return 'n/a';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

const formatPercent = (value: string | null | undefined): string => {
  if (value === null || value === undefined) return 'n/a';
  const num = parseFloat(value);
  if (Number.isNaN(num)) return 'n/a';
  return `${(num * 100).toFixed(2)}%`;
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) return 'n/a';
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const formatAge = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
};

interface MetricCardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  variant?: 'default' | 'success' | 'danger' | 'warning' | 'info';
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, subtitle, icon, variant = 'default' }) => (
  <div className={`metric-card metric-card-${variant} metric-card-medium`}>
    <div className="metric-icon">{icon}</div>
    <div className="metric-content">
      <div className="metric-title">{title}</div>
      <div className="metric-value">{value}</div>
      {subtitle && <div className="metric-subtitle">{subtitle}</div>}
    </div>
  </div>
);

/**
 * Fee-Tier & Volume Tracker current-position panel (spec REQ-009). Mirrors PortfolioSummary's
 * StandaloneSection + MetricCard shape (sibling-first UI reuse).
 */
const FeePositionSummary: React.FC = () => {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['feeTier', 'position', 'latest'],
    queryFn: getFeePositionLatest,
    retry: 2,
  });

  if (isLoading) {
    return (
      <StandaloneSection title="FEE POSITION">
        <div className="chart-loading">
          <div className="loading-spinner"></div>
          <p>Loading fee position...</p>
        </div>
      </StandaloneSection>
    );
  }

  if (isError || !data) {
    return (
      <StandaloneSection title="FEE POSITION">
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <p>Unable to load fee position</p>
          <p className="error-detail">{error instanceof Error ? error.message : 'Please try again later'}</p>
        </div>
      </StandaloneSection>
    );
  }

  return (
    <StandaloneSection title="FEE POSITION">
      <div className="hero-metrics">
        <div className="hero-metric hero-metric-primary">
          <div className="hero-label">Current Tier</div>
          <div className="hero-value-primary">{data.pricingTier}</div>
          <div className="hero-change-neutral">
            {data.qualificationType ? data.qualificationType.replace('FEE_TIER_QUALIFICATION_TYPE_', '') : 'Qualifying factor unavailable'}
          </div>
        </div>

        <div className="hero-metric">
          <div className="hero-label">Maker / Taker Rate</div>
          <div className="hero-value">{formatPercent(data.makerFeeRate)} / {formatPercent(data.takerFeeRate)}</div>
        </div>

        <div className="hero-metric">
          <div className="hero-label">Data Freshness</div>
          <div className="hero-value">{formatAge(data.ageSeconds)}</div>
          {data.ageSeconds > 3600 && <div className="hero-change negative">Stale — check capture Lambda</div>}
        </div>
      </div>

      <div className="metrics-grid">
        <MetricCard
          title="Volume to Next Tier"
          value={formatCurrency(data.volumeToNextTier)}
          subtitle="Remaining trailing volume"
          icon={<TargetIcon size={24} className="icon-text" />}
          variant="info"
        />

        <MetricCard
          title="Headroom Above Current Tier"
          value={formatCurrency(data.headroomAboveCurrentTier)}
          subtitle="Buffer above the tier floor"
          icon={<BarChartIcon size={24} className="icon-text" />}
          variant="success"
        />

        <MetricCard
          title="Projected Tier Loss"
          value={formatDate(data.projectedTierLossDate)}
          subtitle={data.projectedTierLossDate ? 'Run-rate projection' : 'No declining trend detected'}
          icon={<ClockIcon size={24} className="icon-text" />}
          variant={data.projectedTierLossDate ? 'warning' : 'default'}
        />

        <MetricCard
          title="Total Balance"
          value={formatCurrency(data.totalBalance)}
          subtitle={data.hasPromoFee ? 'Promo-adjusted rate active' : 'Organic tier rate'}
          icon={<TrendingUpIcon size={24} className="icon-text" />}
          variant="default"
        />
      </div>
    </StandaloneSection>
  );
};

export default FeePositionSummary;
